import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback, memo } from 'react';
import { List, fromJS, Map } from 'immutable';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import Popups from '../../components/Popup';
import config from '../../config';
import {
  updateField,
  loadSuggestion,
  selectSuggestion,
  closeSuggestion,
  resetForm,
} from '../../actions/popup/buyerPopup';
import { fetchTargetStatuses } from '../../actions/statuses';
import { usePopupContext } from '../../contexts/PopupContext';

/**
 * Add/Update a buyer popup container component.
 *
 * @param props {Object}.
 * @param props.buyer {Immutable.Map} Buyer information.
 * @param props.project {Immutable.Map} Project information.
 * @param props.approval {Immutable.Map} Approval list information.
 * @param props.currentStatus {String} Current status.
 * @param props.highStatus {String} High status.
 * @param props.statuses {Immutable.List} List of statuses.
 * @param props.waiting {Boolean} Show spinner icon if set to true.
 * @param props.activeBuyer {Boolean} Checkbox value of buyer.
 * @param props.overrideCurrent {Boolean} Checkbox value of current status.
 * @param props.overrideHigh {Boolean} Checkbox value of high status.
 * @param props.overrideProject {Boolean} Checkbox value of project.
 * @param props.overrideApproval {Boolean} Checkbox value of approval list.
 * @param props.disableProject {Boolean} If set to true, will disable project.
 * @param props.disableApproval {Boolean} If set to true, will disable approval.
 * @param props.onSave {Function} Function to handle on save button.
 */
const AddUpdateBuyerPopupContainer = ({
  popup,
  buyer,
  project,
  currentStatus,
  highStatus,
  activeBuyer,
  overrideCurrent,
  overrideHigh,
  overrideProject,
  overrideApproval,
  approval,
  updateField,
  loadSuggestion,
  selectSuggestion,
  closeSuggestion,
  resetForm,
  fetchTargetStatuses,
  ...rest
}) => {
  const { onClosePopup, closePopup, startPopupLoading, endPopupLoading } = usePopupContext();
  const [statuses, setStatuses] = useState(List());

  useEffect(() => {
    onClosePopup(resetForm);

    fetchTargetStatuses(({ response: { data } }) => {
      setStatuses(generateStatusList(fromJS(data)));
    });
  }, []);

  const handleClose = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleChange = useCallback(
    ({ event }) => {
      const { name, value, checked } = event.target;
      const checkboxes = ['activeBuyer', 'overrideCurrent', 'overrideHigh', 'overrideProject', 'overrideApproval'];

      updateField(name, checkboxes.includes(name) ? checked : value);
      if (value && value.length >= config.NUMBER_OF_MIN_CHARACTER) {
        if (name === 'project') {
          selectSuggestion(name, null, value);
          selectSuggestion('approval', null, '');
          loadSuggestion(name, value, buyer.get('id'));
        } else if (name === 'approval') {
          selectSuggestion(name, null, value);
          loadSuggestion(name, value, project.get('id'));
        } else if (name === 'buyer') {
          selectSuggestion(name, null, value);
          selectSuggestion('project', null, '');
          selectSuggestion('approval', null, '');
          loadSuggestion(name, value);
        }
      }
    },
    [updateField, loadSuggestion, selectSuggestion, buyer, project],
  );

  const handleSelect = useCallback(
    ({ event, suggestion }) => {
      const { name } = event.target;
      const value = suggestion.get('name');
      const id = suggestion.get('id');

      selectSuggestion(name, id, value);
    },
    [selectSuggestion],
  );

  const handleSuggestionClose = useCallback(
    ({ event }) => {
      const { name } = event.target;

      closeSuggestion(name);
    },
    [closeSuggestion],
  );

  const handleSave = useCallback(() => {
    const onSave = popup.getIn(['props', 'onSave'], () => {});
    const selectedCurrentStatus = statuses.filter(item => item.get('value') === currentStatus).get(0);
    const currentStatusId = statuses.indexOf(selectedCurrentStatus);
    const selectedHighStatus = statuses.filter(item => item.get('value') === highStatus).get(0);
    const highStatusId = statuses.indexOf(selectedHighStatus);
    const refreshGrid = popup.getIn(['props', 'refreshGrid'], () => {});

    onSave(
      {
        buyerId: buyer.get('id'),
        activeBuyer: activeBuyer || undefined,
        currentStatusId,
        overrideCurrentStatus: overrideCurrent,
        highStatusId,
        overrideHighStatus: overrideHigh,
        projectId: project.get('id'),
        overrideProject,
        approvalListId: approval.get('id') === 0 ? null : approval.get('id'),
        overrideApprovalList: overrideApproval,
      },
      {
        beforeStart: startPopupLoading,
        afterSuccess: () => {
          endPopupLoading();
          refreshGrid();
        },
        afterError: ({ responseBody }) => {
          endPopupLoading(responseBody);

          const errors = [];

          Object.keys(responseBody?.errors || {}).forEach(key => {
            errors.push(responseBody.errors[key]);
          });
          updateField(errors);
        },
      },
    );
  }, [
    popup,
    buyer,
    activeBuyer,
    currentStatus,
    overrideCurrent,
    highStatus,
    overrideHigh,
    project,
    overrideProject,
    approval,
    overrideApproval,
    statuses,
    startPopupLoading,
    endPopupLoading,
    updateField,
  ]);

  const canSave = buyer.get('id') && project.get('id') && currentStatus !== '' && highStatus !== '';

  return (
    <Popups.AddUpdateBuyerPopup
      {...rest}
      activeBuyer={activeBuyer}
      approval={approval}
      buyer={buyer}
      canSave={canSave}
      currentStatus={currentStatus}
      highStatus={highStatus}
      onChange={handleChange}
      onClose={handleClose}
      onSave={handleSave}
      onSelect={handleSelect}
      onSuggestionClose={handleSuggestionClose}
      overrideApproval={overrideApproval}
      overrideCurrent={overrideCurrent}
      overrideHigh={overrideHigh}
      overrideProject={overrideProject}
      project={project}
      statuses={statuses}
    />
  );
};

AddUpdateBuyerPopupContainer.propTypes = {
  activeBuyer: PropTypes.bool.isRequired,
  approval: PropTypes.instanceOf(Map).isRequired,
  buyer: PropTypes.instanceOf(Map).isRequired,
  currentStatus: PropTypes.string.isRequired,
  disableApproval: PropTypes.bool.isRequired,
  // statuses: PropTypes.instanceOf(Immutable.List).isRequired,
  disableProject: PropTypes.bool.isRequired,
  errors: PropTypes.instanceOf(List).isRequired,
  highStatus: PropTypes.string.isRequired,
  overrideApproval: PropTypes.bool.isRequired,
  overrideCurrent: PropTypes.bool.isRequired,
  overrideHigh: PropTypes.bool.isRequired,
  overrideProject: PropTypes.bool.isRequired,
  project: PropTypes.instanceOf(Map).isRequired,
  waiting: PropTypes.bool.isRequired,
};

function mapStateToProps(state, props) {
  const form = state.buyerPopup;
  const { popup } = props;

  return {
    buyer: form.get('buyer'),
    project: form.get('project'),
    approval: form.get('approval'),
    currentStatus: form.get('currentStatus'),
    highStatus: form.get('highStatus'),
    // statuses: generateStatusList(statuses),
    waiting: popup.get('isPopupFetching'),
    activeBuyer: form.get('activeBuyer'),
    overrideCurrent: form.get('overrideCurrent'),
    overrideHigh: form.get('overrideHigh'),
    overrideProject: form.get('overrideProject'),
    overrideApproval: form.get('overrideApproval'),
    buyerSuggestions: form.get('buyerSuggestions'),
    projectSuggestions: form.get('projectSuggestions'),
    approvalSuggestions: form.get('approvalSuggestions'),
    disableProject: form.getIn(['buyer', 'id'], 0) <= 0,
    disableApproval: form.getIn(['project', 'id'], 0) <= 0,
    errors: form.get('errors'),
  };
}

/**
 * Recreate target status list.
 *
 * @param statuses {Immutable.List} Status list.
 */
function generateStatusList(statuses) {
  return statuses.map(s => {
    const value = s.get('fc');
    const name = s.get('d');

    return Map({
      value,
      name: `${value} - ${name}`,
    });
  });
}

export default connect(
  mapStateToProps,
  {
    updateField,
    loadSuggestion,
    selectSuggestion,
    closeSuggestion,
    resetForm,
    fetchTargetStatuses,
  },
  mergeProps,
  connectOptions,
)(memo(AddUpdateBuyerPopupContainer));
